import React, { useState, useEffect } from 'react';
import { MessageSquare, Users, AlertTriangle, Search as SearchIcon, Clock, TrendingUp, Megaphone, Pin, X, Send, LayoutList, Monitor, BookOpen, Lightbulb, PenTool, Scale, Building2, HeadphonesIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

// Helper function to render text with styled "Lex" brand
const renderStyledText = (text) => {
  const parts = text.split(/(Lex(?:DoRight|Procure|Delta|Agenda)?)/g);
  return parts.map((part, index) => {
    if (part.startsWith('Lex')) {
      const rest = part.slice(3);
      return (
        <span key={index}>
          <span className="text-[#8B1818] font-semibold">Lex</span>{rest}
        </span>
      );
    }
    return part;
  });
};

const Forum = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('recent');
  const [searchQuery, setSearchQuery] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [forumTopics, setForumTopics] = useState([]);
  const [showNewTopicModal, setShowNewTopicModal] = useState(false);
  const [newTopic, setNewTopic] = useState({
    title: '',
    content: '',
    category: 'applications'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const loadAnnouncements = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/announcements`);
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to load announcements:', error);
    }
  };

  const loadForumTopics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/forum/topics`);
      setForumTopics(response.data);
    } catch (error) {
      console.error('Failed to load forum topics:', error);
    }
  };

  useEffect(() => {
    loadAnnouncements();
    loadForumTopics();
  }, []);

  const handleNewTopic = () => {
    if (!user) {
      navigate('/login');
    } else {
      // For non-admin users viewing announcements category, default to applications
      let defaultCategory = 'applications';
      if (selectedCategory !== 'all' && selectedCategory !== 'announcements') {
        defaultCategory = selectedCategory;
      } else if (selectedCategory === 'announcements' && user?.is_admin) {
        defaultCategory = 'announcements';
      }
      
      setNewTopic({
        title: '',
        content: '',
        category: defaultCategory
      });
      setShowNewTopicModal(true);
    }
  };

  const handleSubmitNewTopic = async (e) => {
    e.preventDefault();
    if (!newTopic.title.trim() || !newTopic.content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    // Double-check: prevent non-admins from posting to announcements
    if (newTopic.category === 'announcements' && !user?.is_admin) {
      toast.error('Only admins can post in the Announcements category');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/forum/topics`, {
        title: newTopic.title,
        content: newTopic.content,
        category: newTopic.category,
        author: user.name || user.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Topic created successfully!');
      setShowNewTopicModal(false);
      setNewTopic({ title: '', content: '', category: 'applications' });
      loadForumTopics();
      
      // Navigate to the new topic
      if (response.data.topic_id) {
        navigate(`/forum/topic/${response.data.topic_id}`);
      }
    } catch (error) {
      console.error('Failed to create topic:', error);
      toast.error('Failed to create topic. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTopicClick = (topicId) => {
    navigate(`/forum/topic/${topicId}`);
  };

  const categories = [
    { id: 'all', name: 'All Topics', icon: LayoutList, description: 'Browse all forum discussions' },
    { id: 'announcements', name: 'Admin Announcements', icon: Megaphone, description: 'Official announcements from LexDoRight team' },
    { id: 'applications', name: 'Applications', icon: Monitor, description: 'Discuss LexProcure PH and other apps that are under development' },
    { id: 'products', name: 'Products & Resources', icon: BookOpen, description: 'eBooks, souvenirs, and materials' },
    { id: 'suggested', name: 'Suggested New Apps/Products', icon: Lightbulb, description: 'Suggest new applications and product ideas' },
    { id: 'blog', name: 'Blog Submissions & Comments', icon: PenTool, description: 'Submit blog ideas and discuss articles' },
    { id: 'legal', name: 'Legal & Policy', icon: Scale, description: 'General legal and policy discussions' },
    { id: 'governance', name: 'Governance', icon: Building2, description: 'Corporate and public governance topics' },
    { id: 'support', name: 'Support & Feedback', icon: HeadphonesIcon, description: 'Technical support and suggestions' },
  ];

  // Use database topics
  const staticTopics = forumTopics.map(t => ({
    id: t.id,
    category: t.category,
    title: t.title,
    content: t.content,
    replies: t.replies || 0,
    views: t.views || 0,
    author: t.author,
    timestamp: new Date(t.created_at).toLocaleDateString()
  }));

  // Combine dynamic announcements with static topics
  const dynamicAnnouncementTopics = announcements.map((ann) => ({
    id: `ann-${ann.id}`,
    category: 'announcements',
    title: ann.title || 'Admin Announcement',
    content: ann.message,
    replies: 0,
    views: 0,
    author: ann.author_name || 'Admin',
    timestamp: new Date(ann.created_at).toLocaleDateString(),
    isPinned: ann.pinned,
    isAdminAnnouncement: true
  }));

  const topics = [...dynamicAnnouncementTopics, ...staticTopics];

  // Filter topics based on category, search, and tab
  const filteredTopics = topics
    .filter(topic => {
      const categoryMatch = selectedCategory === 'all' || topic.category === selectedCategory;
      const searchMatch = searchQuery === '' || 
        topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        topic.author.toLowerCase().includes(searchQuery.toLowerCase());
      return categoryMatch && searchMatch;
    })
    .sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      if (activeTab === 'trending') {
        return b.views - a.views;
      }
      return 0;
    });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="py-12 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Forum
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Connect with the community, share insights, and get support
          </p>
        </div>
      </div>

      {/* Disclaimer Alert */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg shadow-sm">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm text-amber-900">
                <strong>Important:</strong> Information in this forum is for general educational purposes only and does not constitute legal advice. 
                <a href="/forum/guidelines" className="underline ml-1 font-medium">Read full disclaimer and guidelines</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Announcements Banner */}
      {announcements.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-gradient-to-r from-[#8B1818] to-[#701313] rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center mb-4">
              <Megaphone className="h-6 w-6 mr-2" />
              <h2 className="text-xl font-bold">Admin Announcements</h2>
            </div>
            <div className="space-y-3">
              {announcements.slice(0, 3).map((ann) => (
                <div 
                  key={ann.id} 
                  className="bg-white/10 rounded-lg p-4 backdrop-blur cursor-pointer hover:bg-white/20 transition-colors"
                  onClick={() => handleTopicClick(`ann-${ann.id}`)}
                >
                  <div className="flex items-start">
                    {ann.pinned && <Pin className="h-4 w-4 text-[#FDB913] mr-2 mt-0.5 flex-shrink-0" />}
                    <div className="flex-1">
                      <h3 className="font-semibold text-white">{ann.title || 'Announcement'}</h3>
                      <p className="text-white/90 text-sm mt-1 line-clamp-2">{ann.message}</p>
                      <p className="text-white/60 text-xs mt-2">
                        Posted by {ann.author_name} • {new Date(ann.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Categories Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sticky top-4">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Categories</h2>
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                      selectedCategory === category.id
                        ? 'bg-[#8B1818] text-white'
                        : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <category.icon className={`h-5 w-5 ${selectedCategory === category.id ? 'text-white' : 'text-[#8B1818]'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">{category.name}</p>
                        <p className={`text-xs ${selectedCategory === category.id ? 'text-white/80' : 'text-slate-500'}`}>
                          {renderStyledText(category.description)}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Topics List */}
          <div className="lg:col-span-3 space-y-4">
            {/* Tabs and Search */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => setActiveTab('recent')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === 'recent'
                        ? 'bg-white text-[#8B1818] shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Clock className="h-4 w-4 inline mr-2" />
                    Recent Topics
                  </button>
                  <button
                    onClick={() => setActiveTab('trending')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === 'trending'
                        ? 'bg-white text-[#8B1818] shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <TrendingUp className="h-4 w-4 inline mr-2" />
                    Trending
                  </button>
                </div>
                
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search topics..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#8B1818] focus:border-transparent text-sm w-full sm:w-64"
                  />
                </div>
              </div>
            </div>

            {/* Topics List */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-bold text-slate-900 text-lg">
                  {selectedCategory === 'all' ? 'All Topics' : categories.find(c => c.id === selectedCategory)?.name}
                </h2>
                <button 
                  onClick={handleNewTopic}
                  className="bg-[#8B1818] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#701313] transition-colors text-sm"
                >
                  New Topic
                </button>
              </div>
              <div className="divide-y divide-slate-200">
                {filteredTopics.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <p className="text-slate-600">No topics in this category yet. Be the first to start a discussion!</p>
                  </div>
                ) : (
                  filteredTopics.map((topic) => (
                    <div 
                      key={topic.id} 
                      onClick={() => handleTopicClick(topic.id)}
                      className={`px-6 py-4 hover:bg-slate-50 transition-colors cursor-pointer ${topic.isAdminAnnouncement ? 'bg-amber-50/50' : ''}`}
                    >
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${topic.isAdminAnnouncement ? 'bg-[#8B1818]' : 'bg-slate-200'}`}>
                            {topic.isAdminAnnouncement ? (
                              <Megaphone className="h-5 w-5 text-white" />
                            ) : (
                              <MessageSquare className="h-5 w-5 text-slate-600" />
                            )}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {topic.isPinned && <Pin className="h-4 w-4 text-[#8B1818]" />}
                            <h3 className="text-base font-semibold text-slate-900 hover:text-[#8B1818] transition-colors">
                              {topic.title}
                            </h3>
                          </div>
                          <div className="flex items-center space-x-4 mt-2 text-sm text-slate-500">
                            <span className="flex items-center">
                              <Users className="h-4 w-4 mr-1" />
                              {topic.replies} replies
                            </span>
                            <span>{topic.views} views</span>
                            <span>by {topic.author}</span>
                            <span>{topic.timestamp}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* New Topic Modal */}
      {showNewTopicModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-slate-900">Create New Topic</h3>
              <button 
                onClick={() => setShowNewTopicModal(false)} 
                className="text-slate-500 hover:text-slate-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitNewTopic} className="space-y-4">
              <div>
                <label htmlFor="topic-title" className="block text-sm font-medium text-slate-700 mb-2">
                  Topic Title *
                </label>
                <input
                  id="topic-title"
                  type="text"
                  value={newTopic.title}
                  onChange={(e) => setNewTopic({ ...newTopic, title: e.target.value })}
                  placeholder="Enter a descriptive title for your topic"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#8B1818] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label htmlFor="topic-category" className="block text-sm font-medium text-slate-700 mb-2">
                  Category *
                </label>
                <select
                  id="topic-category"
                  value={newTopic.category}
                  onChange={(e) => setNewTopic({ ...newTopic, category: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#8B1818] focus:border-transparent"
                >
                  {user?.is_admin && (
                    <option value="announcements">Admin Announcements (Admin Only)</option>
                  )}
                  <option value="applications">Applications</option>
                  <option value="products">Products & Resources</option>
                  <option value="suggested">Suggested New Apps/Products</option>
                  <option value="blog">Blog Submissions & Comments</option>
                  <option value="legal">Legal & Policy</option>
                  <option value="governance">Governance</option>
                  <option value="support">Support & Feedback</option>
                </select>
              </div>

              <div>
                <label htmlFor="topic-content" className="block text-sm font-medium text-slate-700 mb-2">
                  Content *
                </label>
                <textarea
                  id="topic-content"
                  value={newTopic.content}
                  onChange={(e) => setNewTopic({ ...newTopic, content: e.target.value })}
                  placeholder="Write your topic content here. Be clear and provide enough detail for others to understand and respond."
                  rows={8}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#8B1818] focus:border-transparent resize-none"
                  required
                />
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                <p className="text-sm text-amber-900">
                  <strong>Reminder:</strong> Please ensure your topic follows our community guidelines. Posts that violate guidelines may be edited or removed.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-[#8B1818] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#701313] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    'Creating...'
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Create Topic
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewTopicModal(false)}
                  className="px-6 py-3 border border-slate-300 rounded-lg font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Forum;
